import { PropsWithChildren, ReactNode } from 'react';
import { useCurrentStateAndParams } from '@uirouter/react';

import { AddonLifecycleStatus } from '@api/types.gen';

import { Alert } from '@@/Alert';
import { Widget, WidgetBody } from '@@/Widget';

import { useAddon } from '../queries/useAddon';

import { AddonReleaseScope } from './queries/useAddonReleaseScope';

type Props = {
  scope: AddonReleaseScope;
};

// Release may not exist during mid-operation; failed fetch is expected
const inProgressLabel: Partial<Record<AddonLifecycleStatus, string>> = {
  [AddonLifecycleStatus.ADDON_STATUS_INSTALLING]: 'installing',
  [AddonLifecycleStatus.ADDON_STATUS_UPGRADING]: 'upgrading',
  [AddonLifecycleStatus.ADDON_STATUS_UNINSTALLING]: 'uninstalling',
};

// Gates tabs dependent on Helm release. Shows error if missing; gentler message during operations.
export function AddonReleaseGuard({
  scope,
  children,
}: PropsWithChildren<Props>) {
  const { params } = useCurrentStateAndParams();
  const addonQuery = useAddon(String(params.id ?? ''));
  const status = addonQuery.data?.lifecycleStatus;
  const progress = status ? inProgressLabel[status] : undefined;

  switch (scope) {
    case 'loading':
      return (
        <GuardWidget>
          <WidgetBody loading />
        </GuardWidget>
      );

    case 'environmentError':
      return (
        <GuardMessage color="error" title="Add-on resources cannot be shown">
          Portainer could not work out which environment to read this add-on
          through, so its Kubernetes resources are unavailable. This does not
          affect the add-on itself, which is still installed and running.
        </GuardMessage>
      );

    case 'noEnvironment':
      return (
        <GuardMessage color="info" title="Add-on resources cannot be shown">
          This add-on is installed and running in the cluster Portainer is
          deployed in, but that cluster is not currently connected to Portainer
          as an environment, so its Kubernetes resources cannot be read. Connect
          it as an environment to see them. Installing, upgrading and using the
          add-on are unaffected.
        </GuardMessage>
      );

    case 'missing':
      return progress ? (
        <GuardMessage color="info" title={`Add-on is ${progress}`}>
          This add-on is still {progress}. Its details will appear once the
          operation completes.
        </GuardMessage>
      ) : (
        <GuardMessage color="error" title="Add-on data unavailable">
          The add-on&apos;s Helm release could not be loaded. It may have been
          removed outside Portainer, or the installation or upgrade may have
          failed. Try reinstalling the add-on.
        </GuardMessage>
      );

    case 'release':
    case 'namespaceOnly':
      return <>{children}</>;
  }
}

function GuardMessage({
  color,
  title,
  children,
}: PropsWithChildren<{ color: 'info' | 'error'; title: string }>) {
  return (
    <GuardWidget>
      <WidgetBody>
        <Alert color={color} title={title}>
          {children}
        </Alert>
      </WidgetBody>
    </GuardWidget>
  );
}

function GuardWidget({ children }: { children: ReactNode }) {
  return (
    <div className="row">
      <div className="col-sm-12">
        <Widget>{children}</Widget>
      </div>
    </div>
  );
}
